"use client";

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useSignDoc } from "@/lib/sign/SignDocContext";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * SignDocumentModal — full-screen overlay that renders a PDF in an iframe
 * with a CTA below. Lightweight, presenter-decorative: it does not gate
 * demo flow (pressing N still advances even when open).
 *
 *   Registers itself with SignDocContext on mount so the "D" hotkey
 *   re-opens the most recently registered modal in the current scene.
 *
 *   Two modes:
 *     - sign  (default) → CTA "Sign as …", click stamps a signed receipt
 *                         over the iframe for ~1.2 s, then auto-closes
 *                         and fires onSigned.
 *     - view  (viewOnly) → footer shows "Issued by …" instead of
 *                          "Signatory"; CTA is a plain "Close" button
 *                          (or `actionLabel` if provided); click just
 *                          dismisses, no receipt.
 */
export function SignDocumentModal({
  id,
  open,
  onOpen,
  onClose,
  onSigned,
  pdfPath,
  documentTitle,
  documentRef,
  signedAs,
  signerOrg,
  viewOnly = false,
  actionLabel,
}: {
  id: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSigned?: () => void;
  pdfPath: string;
  documentTitle: string;
  documentRef?: string;
  signedAs: string;
  signerOrg: string;
  viewOnly?: boolean;
  actionLabel?: string;
}) {
  const { register } = useSignDoc();
  const [signed, setSigned] = useState(false);
  const t = useTranslations("signDocumentModal");
  const [signedAt, setSignedAt] = useState<string | null>(null);

  // Register a reopen callback so the global D hotkey can surface this modal.
  useEffect(() => {
    const unregister = register(id, onOpen);
    return unregister;
  }, [id, onOpen, register]);

  // Reset signed state each time the modal opens.
  useEffect(() => {
    if (open) {
      setSigned(false);
      setSignedAt(null);
    }
  }, [open]);

  const close = useCallback(() => {
    onClose();
  }, [onClose]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const sign = useCallback(() => {
    const stamp = new Date().toISOString().replace("T", " ").slice(0, 19) + " UTC";
    setSignedAt(stamp);
    setSigned(true);
    const t = setTimeout(() => {
      onSigned?.();
      onClose();
    }, 1200);
    return () => clearTimeout(t);
  }, [onClose, onSigned]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  // Portal to document.body so the modal escapes any transformed
  // ancestor. .scene-fade on the SceneRouter wrapper applies a
  // `transform: translateY(...)`, which (per CSS spec) creates a
  // containing block for position:fixed descendants — without the
  // portal, the modal positions relative to the scene wrapper rather
  // than the viewport, so it appears off-centre on screens whose
  // content shrinks the wrapper (e.g. T1 3.6 TradeDossier reveal).
  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-orbitra-ink/80 backdrop-blur-sm"
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`signdoc-${id}-title`}
        className="glass relative flex w-[min(70vw,920px)] flex-col gap-3 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label={t("closeAriaLabel")}
          onClick={close}
          className="absolute end-3 top-2 text-[18px] leading-none text-orbitra-muted hover:text-orbitra-paper"
        >
          ×
        </button>

        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {viewOnly ? t("modeView") : t("modeSign")}
            </p>
            <h3
              id={`signdoc-${id}-title`}
              className="mt-1 font-display text-base font-semibold text-orbitra-paper"
            >
              {documentTitle}
            </h3>
            {documentRef && (
              <p className="mt-0.5 font-mono text-[10px] text-orbitra-muted">{documentRef}</p>
            )}
          </div>
          <p className="text-[10px] text-orbitra-muted">
            {t("pressPrefix")} <span className="font-mono text-orbitra-paper">D</span> {t("reopenHint")} <span className="font-mono text-orbitra-paper">Esc</span> {t("dismissSuffix")}
          </p>
        </div>

        <div className="relative h-[60vh] overflow-hidden rounded-md border border-state-verified/30 bg-orbitra-ink/40">
          <iframe
            src={pdfPath}
            title={documentTitle}
            className="h-full w-full"
          />
          {signed && (
            <div className="absolute inset-0 grid place-items-center bg-orbitra-ink/85 backdrop-blur-sm">
              <div className="glass flex flex-col items-center gap-2 px-8 py-6 text-center">
                <span className="rounded-full border border-policy-pass/40 bg-policy-pass/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-policy-pass">
                  {t("signedChip")}
                </span>
                <p className="mt-2 font-display text-base font-semibold text-orbitra-paper">
                  {signedAs}
                </p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
                  {signerOrg}
                </p>
                {signedAt && (
                  <p className="mt-1 font-mono text-[10px] text-orbitra-muted">{signedAt}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col text-[10px]">
            <span className="font-mono uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {viewOnly ? t("issuedByLabel") : t("signatoryLabel")}
            </span>
            <span className="text-orbitra-paper">{signedAs}</span>
            <span className="text-orbitra-muted">{signerOrg}</span>
          </div>
          {viewOnly ? (
            <button
              type="button"
              onClick={close}
              className="cta-primary px-5 py-2.5 font-display text-sm font-semibold"
            >
              {actionLabel ?? t("closeButton")}
            </button>
          ) : (
            <button
              type="button"
              disabled={signed}
              onClick={sign}
              className={clsx(
                "cta-primary px-5 py-2.5 font-display text-sm font-semibold",
                signed && "cursor-not-allowed opacity-60",
              )}
            >
              {signed ? t("signedButton") : (actionLabel ?? (t as (k: string, p?: Record<string, string | number>) => string)("signAsDefault", { name: signedAs }))}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
